import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { Download, FileDownload } from '@mui/icons-material';
import { exportService } from '../../services/export.service';
import { ExportOptions, ExportStats } from '../../types/export.types';
import { LeadFilters } from '../../types/lead.types';

interface LeadExportDialogProps {
  open: boolean;
  onClose: () => void;
  currentFilters?: LeadFilters;
}

export const LeadExportDialog: React.FC<LeadExportDialogProps> = ({
  open,
  onClose,
  currentFilters,
}) => {
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<ExportStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (open) {
      // Wrap in try-catch to prevent crashes
      try {
        loadStats();
      } catch (err) {
        console.error('Error in useEffect:', err);
      }
    }
  }, [open]);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const data = await exportService.getExportStats();
      setStats(data);
    } catch (err: any) {
      // Silently fail - stats are not critical for export
      console.log('Stats loading failed, but export will still work');
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    try {
      const options: ExportOptions = {
        format,
        filters: currentFilters
          ? {
              status: currentFilters.status,
              priority: currentFilters.priority,
              assignedTo: currentFilters.assigned_to ? [currentFilters.assigned_to] : undefined,
              dateFrom: currentFilters.date_from,
              dateTo: currentFilters.date_to,
              search: currentFilters.search,
            }
          : undefined,
      };

      const { blob, filename } = await exportService.exportLeads(options);
      exportService.downloadFile(blob, filename);

      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export leads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileDownload />
          <Typography component="span">Export Leads</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            <Typography component="span">{error}</Typography>
          </Alert>
        )}

        {/* Export Statistics */}
        {loadingStats && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {!loadingStats && stats && stats.totalLeads > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography component="div" variant="subtitle2" gutterBottom>
              Export Summary
            </Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <Chip label={`Total: ${stats.totalLeads}`} color="primary" size="small" />
              {Object.entries(stats.byStatus).map(([status, count]) => (
                <Chip key={status} label={`${status}: ${count}`} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}

        {/* Format Selection */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel component="legend">Export Format</FormLabel>
          <RadioGroup value={format} onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}>
            <FormControlLabel
              value="csv"
              control={<Radio />}
              label={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography component="span" variant="body1" sx={{ display: 'block' }}>CSV (Comma Separated Values)</Typography>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Best for Excel, Google Sheets, and data analysis
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="json"
              control={<Radio />}
              label={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography component="span" variant="body1" sx={{ display: 'block' }}>JSON (JavaScript Object Notation)</Typography>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Best for developers and API integrations
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Current Filters Info */}
        {currentFilters && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography component="span" variant="body2">
              Export will include only the leads matching your current filters
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Download />}
          disabled={loading}
        >
          {loading ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};